import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function CategoryPage() {
  const { category } = useParams();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  // Format category name for display
  const categoryName = category
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
    .replace('And', '&');

  useEffect(() => {
    fetchArticles();
  }, [category]);

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${API}/articles`);
      // Filter articles by category
      const filtered = response.data.filter(
        article => article.category.toLowerCase().replace(/\s+/g, '-').replace('&', 'and') === category && article.published
      );
      setArticles(filtered);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching articles:', err);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Loading articles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-lg border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white/20">
              <img
                src="https://customer-assets.emergentagent.com/job_b9ac4c36-714b-45c0-9160-f047c039178c/artifacts/vio7h01p_image.png"
                alt="John Ghatti"
                className="w-full h-full object-cover"
              />
            </div>
            <span className="text-xl font-light tracking-wide">JOHN GHATTI</span>
          </Link>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 border border-zinc-700 text-sm hover:bg-zinc-800 transition-colors"
          >
            ← Back
          </button>
        </div>
      </header>

      {/* Category Content */}
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Category Header */}
          <div className="mb-12">
            <div className="inline-block px-4 py-1 bg-white text-black text-xs font-bold tracking-widest mb-6">
              {categoryName.toUpperCase()}
            </div>
            <h1 className="text-5xl md:text-6xl font-light mb-4">
              {categoryName} Articles
            </h1>
            <p className="text-gray-400 font-light text-lg">
              {articles.length} {articles.length === 1 ? 'article' : 'articles'} in this category
            </p>
          </div>

          {articles.length === 0 ? (
            /* No Articles */
            <div className="text-center py-20">
              <svg className="w-24 h-24 mx-auto mb-8 text-zinc-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h2 className="text-3xl font-light mb-4">No Articles Yet</h2>
              <p className="text-gray-400 mb-8">Check back soon for new content in this category.</p>
              <Link
                to="/"
                className="inline-block px-8 py-3 bg-white text-black font-bold hover:bg-gray-200 transition-colors"
              >
                Back to Home
              </Link>
            </div>
          ) : (
            /* Articles Grid */
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {articles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-zinc-950 border border-zinc-800 hover:border-zinc-700 transition-all duration-300 overflow-hidden"
                >
                  {/* Featured Image */}
                  {article.featured_image && (
                    <div className="relative h-56 overflow-hidden">
                      <img
                        src={article.featured_image}
                        alt={article.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/10 to-transparent"></div>
                    </div>
                  )}

                  {/* Content */}
                  <div className="p-6">
                    {/* Category */}
                    <span className="inline-block px-2 py-0.5 bg-zinc-800 text-white text-xs font-bold tracking-wider mb-3">
                      {article.category.toUpperCase()}
                    </span>

                    {/* Title */}
                    <h3 className="text-xl font-light mb-3 group-hover:text-gray-300 transition-colors leading-tight line-clamp-2">
                      {article.title}
                    </h3>

                    {/* Excerpt */}
                    <p className="text-gray-400 font-light text-sm leading-relaxed mb-4 line-clamp-3">
                      {article.excerpt}
                    </p>

                    {/* Meta Info */}
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        <span>{article.view_count} views</span>
                      </div>
                      <span>•</span>
                      <span>{new Date(article.created_at).toLocaleDateString()}</span>
                    </div>

                    {/* Read More */}
                    <div className="mt-4 inline-flex items-center gap-2 text-xs font-bold tracking-widest group-hover:gap-3 transition-all duration-300">
                      READ MORE
                      <span>→</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* Back to Home */}
          <div className="mt-12 text-center">
            <Link
              to="/"
              className="inline-block px-8 py-3 border border-zinc-700 text-sm hover:bg-zinc-900 transition-colors"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CategoryPage;
